/**
 * useReceiptCart — state management для кошика POS-чека.
 *
 * Тримає масив items + обчислює subtotal/total. Без побічних ефектів —
 * комітинг до backend через окремий submit (POSPaymentDialog).
 */
import { useCallback, useMemo, useState } from 'react';

export interface CartItem {
  // Локальний key, оскільки одна номенклатура може двічі бути у чеку.
  key: string;
  nomenclature_id: string;
  name: string;
  item_kind: 'goods' | 'service';
  quantity: number;
  price_per_unit: number;
  vat_rate: number; // %, наприклад 20.00
}

export interface CartSummary {
  itemsCount: number;
  subtotal: number;
  total: number;
}

export function useReceiptCart() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [bonusWriteoff, setBonusWriteoff] = useState(0);

  const addItem = useCallback((entry: Omit<CartItem, 'key' | 'quantity'> & { quantity?: number }) => {
    setItems((prev) => {
      // Якщо така номенклатура вже у кошику — збільшимо qty
      const existing = prev.find((p) => p.nomenclature_id === entry.nomenclature_id);
      if (existing) {
        return prev.map((p) =>
          p.key === existing.key ? { ...p, quantity: p.quantity + (entry.quantity ?? 1) } : p,
        );
      }
      return [
        ...prev,
        {
          ...entry,
          key: crypto.randomUUID(),
          quantity: entry.quantity ?? 1,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((key: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((p) => p.key !== key));
      return;
    }
    setItems((prev) => prev.map((p) => (p.key === key ? { ...p, quantity } : p)));
  }, []);

  const removeItem = useCallback((key: string) => {
    setItems((prev) => prev.filter((p) => p.key !== key));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    setDiscountAmount(0);
    setBonusWriteoff(0);
  }, []);

  const summary: CartSummary = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + it.quantity * it.price_per_unit, 0);
    const total = Math.max(0, subtotal - discountAmount - bonusWriteoff);
    return {
      itemsCount: items.length,
      subtotal,
      total,
    };
  }, [items, discountAmount, bonusWriteoff]);

  return {
    items,
    summary,
    isEmpty: items.length === 0,
    discountAmount,
    setDiscountAmount,
    bonusWriteoff,
    setBonusWriteoff,
    addItem,
    updateQuantity,
    removeItem,
    clear,
  };
}

export type ReceiptCart = ReturnType<typeof useReceiptCart>;
